import React from "react";
import SkeletonComponent from "../Skeleton";
import AutoSuggest from "Components/Form/AutoSuggest";
import { useQuery } from "@tanstack/react-query";
import TimezoneService from "Services/timezone.service";
import { useAppState } from "Context/StateContext";
import { useTranslation } from "react-i18next";
import { Timezone } from "Models/timezone";
import { formatGMT } from "Util/Format";

export interface AppTimezoneSelectProps {
  isLoading?: boolean;
  isReadonly?: boolean;
  isRequired?: boolean;
  isDisabled?: boolean;
  textValue?: string;
  value?: Timezone;
  hasError?: boolean;
  errorMessage?: string;
  handleChange?: (value: Timezone | undefined) => void;
  hasShowAutoUpdateTimezoneByCurrentUserBroswerLabel?: boolean;
}

export const AppTimezoneSelect = (props: AppTimezoneSelectProps) => {
  const {
    isReadonly,
    isRequired,
    isDisabled,
    textValue,
    value,
    hasError,
    errorMessage,
    handleChange,
    isLoading: isLoadingOutside,
    hasShowAutoUpdateTimezoneByCurrentUserBroswerLabel = true,
  } = props;
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const { isLoading, data: timezones } = useQuery(
    ["timezones.list.items"],
    () =>
      TimezoneService.getAll({
        sortBy: JSON.stringify(["gmtOffset"]),
      }).then((response) => response.data),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
    }
  );

  return (
    <SkeletonComponent isLoading={isLoading || isLoadingOutside}>
      <>
        <AutoSuggest
          label={translate("timezone")}
          disabled={isDisabled || isReadonly}
          placeholder={translate("searchForTimezone")}
          inputValue={textValue ?? value?.nameFormatted}
          value={value}
          options={timezones}
          dataCy="timezone"
          error={hasError}
          errorMessage={errorMessage}
          getOptionLabel={(time: Timezone) =>
            time && time.gmtOffset && time.nameFormatted
              ? `GMT (${formatGMT(time?.gmtOffset)}) ${time?.nameFormatted
                  ?.replace("/", " / ")
                  .replace("_", "")}`
              : ""
          }
          getOptionSelected={(option: Timezone, value: Timezone) =>
            option && value && option.id === value.id
          }
          onChange={(value: Timezone) => {
            handleChange?.(value || undefined);
          }}
          required={isRequired}
          getOptionKey={(option) => {
            return option?.id || 0;
          }}
        />
        {hasShowAutoUpdateTimezoneByCurrentUserBroswerLabel && <span style={{ color: "#465869", fontSize: 12, lineHeight: "24px" }}>
          {translate("timezoneIsUpdatedAutomaticallyToMatchYourComputerTimezone")}
        </span>}
      </>
    </SkeletonComponent>
  );
};
